import React, { useRef, useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../../services/apiConfig";
import { useParams, useNavigate } from "react-router-dom";
import SuccessModal from "../../Common/SuccessModal";
import ErrorModal from "../../Common/ErrorModal";
import InfoModal from "../../Common/InfoModal";
import {
  ArrowLeft,
  Clock,
  Users,
  Gavel,
  Trophy,
  AlertCircle,
  CheckCircle,
  Timer,
  Eye,
  Activity,
  StopCircle,
  Contact,
  Edit,
  Building2,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import "./AuctioneerLiveView.css";
import apiDencrimentValue from "../../../services/apiDencrimentValue";
import { rankLabel } from "../../../utils/auctionDirection";

interface AuctionCreator {
  company_name: string;
  person_name: string;
  phone: string;
}

interface AuctionParticipant {
  id: number;
  user_id: number | null;
  phone_number: string;
  status: string;
  invited_at: string;
  joined_at: string | null;
  person_name: string | null;
  company_name: string | null;
}

interface Bid {
  id: number;
  user_id: number;
  amount: string;
  bid_time: string;
  created_at: string;
  person_name?: string;
  company_name?: string;
  user?: {
    person_name: string;
    company_name: string;
  } | null;
}

interface Auction {
  id: number;
  title: string;
  description: string;
  auction_date: string;
  start_time: string;
  end_time: string;
  duration: number;
  currency: string;
  current_price: string;
  decremental_value: string;
  auction_mode?: "reverse" | "forward";
  status: "upcoming" | "live" | "completed";
  auction_no: string;
  formatted_start_time: string;
  formatted_end_time: string;
  time_remaining: number;
  creator_info: AuctionCreator;
  participants: AuctionParticipant[];
  bids: Bid[];
  documents: any[];
  statistics: {
    total_participants: number;
    total_bids: number;
    active_participants: number;
    highest_bid: number | null;
    lowest_bid: number | null;
  };
}

const AuctioneerLiveView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const [isEndingAuction, setIsEndingAuction] = useState(false);
  const [isExtendingTime, setIsExtendingTime] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastBidCount, setLastBidCount] = useState<number>(0);

  const [newDecrementValue, setNewDecrementValue] = useState<number | string>(
    ""
  );
  const [updatingDecrement, setUpdatingDecrement] = useState(false);
  const [isEditDecrementOpen, setIsEditDecrementOpen] = useState(false);
  const [rejectingBidId, setRejectingBidId] = useState<number | null>(null);
  const [lastAutoExtendTime, setLastAutoExtendTime] = useState<number>(0);
  const [currentEndTime, setCurrentEndTime] = useState<Date | null>(null);
  const [isAutoExtending, setIsAutoExtending] = useState(false);
  const [initialStartPrice, setInitialStartPrice] = useState<string | null>(null);
  
  // Use ref to store the LOCKED start price - this NEVER changes once set
  // Initialize from localStorage if available
  const lockedStartPriceRef = useRef<string | null>(
    localStorage.getItem(`firstBid_${id}`) || null
  );

  // Time extension tracking
  const previousEndTimeRef = useRef<string | null>(null);
  const [showTimeExtensionModal, setShowTimeExtensionModal] = useState(false);
  const [timeExtensionMessage, setTimeExtensionMessage] = useState("");

  // Modal states
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: "Success",
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
  });
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    message: "",
  });

  const prevBidCountRef = useRef(0);

  // Initialize first bid from localStorage on mount
  useEffect(() => {
    const storedFirstBid = localStorage.getItem(`firstBid_${id}`);
    if (storedFirstBid) {
      setInitialStartPrice(storedFirstBid);
    }
  }, [id]);

  const fetchAuctionData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/auction/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Could not retrieve auction details.");

      setAuction(data.auction);

      if (data.auction.auction_date && data.auction.end_time) {
        const newEnd = new Date(
          `${data.auction.auction_date}T${data.auction.end_time}`
        );
        setCurrentEndTime(newEnd);
      }

      // Set initial start price logic - LOCKED ONCE AND NEVER CHANGED
      // Store directly in ref which persists across ALL renders AND in localStorage
      if (lockedStartPriceRef.current === null) {
        if (data.auction.bids && data.auction.bids.length > 0) {
          // Sort bids by created_at to get the very first bid (chronologically)
          const sortedBids = [...data.auction.bids].sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          const firstBid = sortedBids[0];
          
          // Lock to the first bid amount in ref - this will NEVER change
          lockedStartPriceRef.current = firstBid.amount;
          // Save to localStorage so it persists across page refreshes
          localStorage.setItem(`firstBid_${id}`, firstBid.amount);
          setInitialStartPrice(firstBid.amount);
        } else {
          // No bids yet, use current_price as placeholder (will lock when first bid arrives)
          setInitialStartPrice(data.auction.current_price);
        }
      } else {
        // Already locked in ref - use the locked value and ignore any changes
        if (initialStartPrice !== lockedStartPriceRef.current) {
          setInitialStartPrice(lockedStartPriceRef.current);
        }
      }

      // Set bid count for initial/load
      if (lastBidCount === 0) setLastBidCount(data.auction.bids.length);

      if (data.auction.status === "completed") {
        setInfoModal({
          isOpen: true,
          message: "🏁 This auction has been completed.",
        });
      }
      setError(null);
    } catch (err: any) {
      setError(err.message);
      setErrorModal({
        isOpen: true,
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuctionData();
    // Reduced from 15 seconds to 5 seconds for faster bid updates
    const interval = setInterval(fetchAuctionData, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const handleExtendTimeAPI = useCallback(
    async (reason = "manual") => {
      setIsAutoExtending(true);
      try {
        const token =
          localStorage.getItem("authToken") || localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/auction/${id}/extend`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ additional_minutes: 3 }),
        });
        const result = await response.json();
        if (!response.ok || !result.success)
          throw new Error(result.message || "Failed to extend time.");

        setSuccessModal({
          isOpen: true,
          title: "Time Extended",
          message:
            reason === "auto"
              ? "⏱️ New bid detected! Time automatically extended by 3 minutes."
              : "Time extended successfully by 3 minutes!",
        });

        if (currentEndTime)
          setCurrentEndTime(new Date(currentEndTime.getTime() + 3 * 60 * 1000));
        setLastAutoExtendTime(Date.now());
        setTimeout(fetchAuctionData, 2000);
      } catch (err: any) {
        setErrorModal({
          isOpen: true,
          message: err.message,
        });
      } finally {
        setIsAutoExtending(false);
      }
    },
    [id, currentEndTime]
  );

  // Separate function for auto-extend
  const handleAutoExtendTime = useCallback(async () => {
    if (!id || isAutoExtending) return;

    setIsAutoExtending(true);

    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/auction/${id}/extend`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ additional_minutes: 3 }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessModal({
          isOpen: true,
          title: "Time Extended Automatically",
          message:
            "⏱️ New bid detected! Time automatically extended by 3 minutes.",
        });

        // Immediately update the local end time by adding 3 minutes
        if (currentEndTime) {
          const newEndTime = new Date(currentEndTime.getTime() + 3 * 60 * 1000);
          setCurrentEndTime(newEndTime);
        }

        setLastAutoExtendTime(Date.now());

        // Refresh auction data to get updated end_time from server
        setTimeout(() => {
          fetchAuctionData();
        }, 2000);
      } else {
        throw new Error(result.message || "Failed to extend time.");
      }
    } catch (err: any) {
      console.error("❌ Auto-extend failed:", err);
      setErrorModal({
        isOpen: true,
        message: err.message,
      });
    } finally {
      setIsAutoExtending(false);
    }
  }, [id, currentEndTime, isAutoExtending]);

  // Auto-extend time when new bid is placed in last 3 minutes
  useEffect(() => {
    if (!auction || auction.status !== "live" || isPaused || !currentEndTime)
      return;
    const curBids = auction.bids.length;
    const prevBids = prevBidCountRef.current;

    const now = new Date();
    const remainingSeconds = Math.floor(
      (currentEndTime.getTime() - now.getTime()) / 1000
    );
    const cooldownEnough = Date.now() - lastAutoExtendTime > 30000;

    // Log for debugging

    // Auto-extend when bid is placed in last 3 minutes (180 seconds)
    if (
      curBids > prevBids &&
      remainingSeconds > 0 &&
      remainingSeconds <= 180 &&
      cooldownEnough &&
      !isAutoExtending
    ) {
      handleExtendTimeAPI("auto");
    } else if (curBids > prevBids && remainingSeconds > 180) {
    }

    prevBidCountRef.current = curBids;
    setLastBidCount(curBids);
  }, [
    auction?.bids,
    auction,
    isPaused,
    handleExtendTimeAPI,
    lastAutoExtendTime,
    currentEndTime,
    isAutoExtending,
  ]);

  // Countdown timer using currentEndTime
  useEffect(() => {
    if (!auction || auction.status !== "live" || isPaused || !currentEndTime) {
      if (auction?.status === "completed")
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }
    const timer = setInterval(() => {
      const now = new Date();
      const diff = currentEndTime.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
        if (auction.status !== "completed") {
          setAuction((prev) =>
            prev ? { ...prev, status: "completed" } : null
          );
          setInfoModal({
            isOpen: true,
            message: "🏁 Auction time has ended!",
          });
        }
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
    return () => clearInterval(timer);
  }, [auction, isPaused, currentEndTime]);

  // Reset when auction status changes
  useEffect(() => {
    if (auction?.status !== "live") {
      setLastAutoExtendTime(0);
    }
  }, [auction?.status]);

  // Monitor for time extension (when end_time changes)
  useEffect(() => {
    if (!auction || auction.status !== "live") return;

    const currentEndTime = auction.end_time;
    
    // Check if end time has changed (indicating an extension)
    if (previousEndTimeRef.current && previousEndTimeRef.current !== currentEndTime) {
      // Calculate time remaining
      const endTime = new Date(`${auction.auction_date}T${currentEndTime}`);
      const now = new Date();
      const timeRemaining = endTime.getTime() - now.getTime();
      const minutesRemaining = Math.floor(timeRemaining / (1000 * 60));
      
      // Show extension modal
      setTimeExtensionMessage(
        `Auction time has been extended! A bid was placed in the last 3 minutes. New end time: ${formatTime(currentEndTime)}. Time remaining: ${minutesRemaining} minutes.`
      );
      setShowTimeExtensionModal(true);
      
      // Auto-close modal after 8 seconds
      setTimeout(() => {
        setShowTimeExtensionModal(false);
      }, 8000);
    }
    
    previousEndTimeRef.current = currentEndTime;
  }, [auction?.end_time, auction?.status, auction?.auction_date]);

  // Helper function to format time
  const formatTime = (timeStr: string) => {
    if (!timeStr) return "N/A";
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const handlePauseAuction = () => setIsPaused(!isPaused);

  const handleEndAuction = async () => {
    if (!id || !window.confirm("Are you sure you want to end this auction?")) {
      return;
    }

    setIsEndingAuction(true);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/auction/${id}/close`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setAuction((prev) => (prev ? { ...prev, status: "completed" } : null));
        setSuccessModal({
          isOpen: true,
          title: "Auction Ended",
          message:
            result.message || "🛑 Auction has been successfully ended.",
        });
        fetchAuctionData();
      } else {
        throw new Error(result.message || "Failed to end the auction.");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err.message,
      });
      console.error("Failed to end auction:", err);
    } finally {
      setIsEndingAuction(false);
    }
  };

  const handleExtendTime = async () => {
    if (!id) return;

    setIsExtendingTime(true);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/auction/${id}/extend`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ additional_minutes: 3 }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessModal({
          isOpen: true,
          title: "Time Extended",
          message:
            result.message || "Time extended successfully by 3 minutes!",
        });

        // Immediately update local end time
        if (currentEndTime) {
          const newEndTime = new Date(currentEndTime.getTime() + 3 * 60 * 1000);
          setCurrentEndTime(newEndTime);
        }

        setLastAutoExtendTime(Date.now());
        fetchAuctionData();
      } else {
        throw new Error(result.message || "Failed to extend time.");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err.message,
      });
      console.error("Failed to extend time:", err);
    } finally {
      setIsExtendingTime(false);
    }
  };

  // Updated reject bid function - using bid ID instead of auction ID
  const handleRejectBid = async (bidId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to reject this bid? This action cannot be undone."
      )
    ) {
      return;
    }

    setRejectingBidId(bidId);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");

      // Correct API endpoint - using bid ID instead of auction ID
      const response = await fetch(
        `${API_BASE_URL}/auction/prebid/${bidId}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            bid_id: bidId,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessModal({
          isOpen: true,
          title: "Bid Rejected",
          message: result.message || "Bid has been rejected successfully.",
        });
        fetchAuctionData(); // Refresh data to update the bids list
      } else {
        throw new Error(result.message || "Failed to reject bid.");
      }
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        message: err.message,
      });
      console.error("Failed to reject bid:", err);
    } finally {
      setRejectingBidId(null);
    }
  };

  if (loading && !auction) {
    return (
      <div className="alv-loading">
        <div className="alv-loading-spinner" />
        <p>Loading live auction monitoring...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alv-error">
        <AlertCircle className="alv-error-icon" />
        <h2>Access Denied</h2>
        <p>{error}</p>
        <button
          onClick={() => navigate("/dashboard/auctions")}
          className="alv-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to My Auctions
        </button>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="alv-error">
        <AlertCircle className="alv-error-icon" />
        <h2>Auction Not Found</h2>
        <p>The requested auction could not be found.</p>
        <button
          onClick={() => navigate("/dashboard/auctions")}
          className="alv-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to My Auctions
        </button>
      </div>
    );
  }

  // Group bids by user to show only latest bid from each participant
  const bidsByUser = new Map<number, { bid: Bid; totalBids: number }>();
  
  auction.bids.forEach((bid) => {
    const userId = bid.user_id;
    if (!bidsByUser.has(userId)) {
      // Count total bids from this user
      const userBidCount = auction.bids.filter(b => b.user_id === userId).length;
      bidsByUser.set(userId, { bid, totalBids: userBidCount });
    } else {
      const existing = bidsByUser.get(userId)!;
      // Keep the latest bid (most recent timestamp)
      const existingTime = new Date(existing.bid.bid_time || existing.bid.created_at).getTime();
      const newTime = new Date(bid.bid_time || bid.created_at).getTime();
      
      if (newTime > existingTime) {
        bidsByUser.set(userId, { bid, totalBids: existing.totalBids });
      }
    }
  });
  
  // Convert to array and sort best-first (lowest first for reverse, highest first for forward)
  const isForwardAuction = auction.auction_mode === "forward";
  const rankedBids = Array.from(bidsByUser.values())
    .map(item => ({ ...item.bid, totalBids: item.totalBids }))
    .sort((a, b) => (isForwardAuction ? -1 : 1) * (parseFloat(a.amount) - parseFloat(b.amount)));

  // Calculate if we're in auto-extend window
  const isInAutoExtendWindow =
    timeLeft.minutes < 3 &&
    timeLeft.days === 0 &&
    timeLeft.hours === 0 &&
    auction.status === "live";

  const lowestBid = isForwardAuction ? auction.statistics.highest_bid : auction.statistics.lowest_bid;
  const statusClass = isPaused ? "paused" : auction.status;
  const formatBidTime = (b: Bid) => {
    const d = new Date(b.bid_time || b.created_at);
    return {
      date: d.toLocaleDateString("en-GB"),
      time: d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    };
  };

  return (
    <div className="alv-container">
      <button onClick={() => navigate(-1)} className="alv-back-btn">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <header className="alv-header">
        <div className="alv-header-info">
          <h1 className="alv-title">{auction.title}</h1>
          <div className="alv-meta">
            <span className="alv-auction-no">
              <Gavel /> {auction.auction_no}
            </span>
            <span className="alv-meta-dot" />
            <span className="alv-meta-text">
              {new Date(auction.auction_date).toLocaleDateString("en-GB")} ·{" "}
              {formatTime(auction.start_time)} – {formatTime(auction.end_time)}
            </span>
          </div>
          {auction.description && (
            <p className="alv-description">{auction.description}</p>
          )}
        </div>
        <div className="alv-header-actions">
          <div className={`alv-status-badge alv-status-${statusClass}`}>
            <div
              className={`alv-live-indicator ${
                auction.status !== "live" ? "alv-paused" : ""
              }`}
            />
            {isPaused ? "PAUSED" : auction.status.toUpperCase()}
          </div>
        </div>
      </header>

      <section className="alv-hero">
        <div className="alv-hero-timer">
          <div className="alv-hero-label">
            <Timer />
            {auction.status === "completed"
              ? "Auction Closed"
              : "Auction closes in"}
          </div>
          <div className="alv-countdown-display">
            <div className="alv-countdown-segment">
              <span className="alv-countdown-number">{timeLeft.days}</span>
              <span className="alv-countdown-label">Days</span>
            </div>
            <div className="alv-countdown-separator">:</div>
            <div className="alv-countdown-segment">
              <span className="alv-countdown-number">
                {timeLeft.hours.toString().padStart(2, "0")}
              </span>
              <span className="alv-countdown-label">Hours</span>
            </div>
            <div className="alv-countdown-separator">:</div>
            <div className="alv-countdown-segment">
              <span className="alv-countdown-number">
                {timeLeft.minutes.toString().padStart(2, "0")}
              </span>
              <span className="alv-countdown-label">Min</span>
            </div>
            <div className="alv-countdown-separator">:</div>
            <div className="alv-countdown-segment">
              <span className="alv-countdown-number">
                {timeLeft.seconds.toString().padStart(2, "0")}
              </span>
              <span className="alv-countdown-label">Sec</span>
            </div>
          </div>
          <p className="alv-countdown-subtitle">
            {isPaused && "Auction is currently paused"}
            {!isPaused && isInAutoExtendWindow && (
              <span className="alv-auto-active">
                Auto-extend is active (last 3 minutes)
              </span>
            )}
            {!isPaused &&
              !isInAutoExtendWindow &&
              auction.status === "live" && (
                <span className="alv-auto-idle">
                  Auto-extend activates in the last 3 minutes
                </span>
              )}
          </p>
        </div>

        <div className="alv-hero-controls">
          <div className="alv-hero-label">Auction Controls</div>
          <button
            onClick={handleExtendTime}
            className="alv-control-btn alv-extend-btn"
            disabled={auction.status !== "live" || isExtendingTime}
          >
            {isExtendingTime ? (
              <div className="alv-loading-spinner-small" />
            ) : (
              <Clock className="w-4 h-4" />
            )}
            {isExtendingTime ? "Extending..." : "Extend Time by 3 Min"}
          </button>
          <button
            onClick={handleEndAuction}
            className="alv-control-btn alv-end-btn"
            disabled={auction.status === "completed" || isEndingAuction}
          >
            {isEndingAuction ? (
              <div className="alv-loading-spinner-small" />
            ) : (
              <StopCircle className="w-4 h-4" />
            )}
            {isEndingAuction ? "Ending..." : "End Auction"}
          </button>
        </div>
      </section>

      <section className="alv-stats">
        <div className="alv-stat alv-stat-lowest">
          <span className="alv-stat-label">{isForwardAuction ? "Current Highest" : "Current Lowest"}</span>
          <span className="alv-stat-value">
            {lowestBid !== null
              ? `${auction.currency} ${lowestBid.toLocaleString()}`
              : "No bids yet"}
          </span>
          <span className="alv-stat-hint">Leading offer right now</span>
        </div>
        <div className="alv-stat">
          <span className="alv-stat-label">First Bid</span>
          <span className="alv-stat-value">
            {lockedStartPriceRef.current
              ? `${auction.currency} ${parseFloat(
                  lockedStartPriceRef.current
                ).toLocaleString()}`
              : "Waiting..."}
          </span>
          <span className="alv-stat-hint">Locked once the first bid arrives</span>
        </div>
        <div className="alv-stat">
          <span className="alv-stat-label">Decremental Value</span>
          <span className="alv-stat-value">
            {auction.currency}{" "}
            {parseFloat(auction.decremental_value).toLocaleString()}
          </span>
          <button
            className="alv-stat-action"
            onClick={() => {
              setNewDecrementValue(auction.decremental_value || 0);
              setIsEditDecrementOpen(true);
            }}
          >
            <Edit /> Edit value
          </button>
        </div>
        <div className="alv-stat">
          <span className="alv-stat-label">Participation</span>
          <span className="alv-stat-value">
            {auction.statistics.active_participants} /{" "}
            {auction.statistics.total_participants}
          </span>
          <span className="alv-stat-hint">
            {auction.statistics.total_bids} bids placed
          </span>
        </div>
      </section>

      {isEditDecrementOpen && (
        <div
          className="alv-modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="alv-modal">
            <h3 className="alv-modal-title">Edit Decrement Value</h3>
            <div>
              <label className="alv-modal-label">
                Value ({auction?.currency || "₹"})
              </label>
              <input
                type="number"
                className="alv-modal-input"
                value={String(newDecrementValue)}
                onChange={(e) => setNewDecrementValue(e.target.value)}
                disabled={updatingDecrement}
              />
            </div>
            <div className="alv-modal-actions">
              <button
                className="alv-btn alv-btn-secondary"
                onClick={() => setIsEditDecrementOpen(false)}
                disabled={updatingDecrement}
              >
                Cancel
              </button>
              <button
                className="alv-btn alv-btn-primary"
                onClick={async () => {
                  const num = Number(newDecrementValue);
                  if (Number.isNaN(num) || num <= 0) {
                    setErrorModal({
                      isOpen: true,
                      message: "Please enter a valid positive number.",
                    });
                    return;
                  }
                  setUpdatingDecrement(true);
                  try {
                    const resp =
                      await apiDencrimentValue.updateAuctionDecrement(
                        id || "",
                        num
                      );
                    if (resp && resp.success) {
                      setSuccessModal({
                        isOpen: true,
                        title: "Updated",
                        message: "Decrement value updated successfully!",
                      });
                      await fetchAuctionData();
                      setIsEditDecrementOpen(false);
                    } else {
                      setErrorModal({
                        isOpen: true,
                        message: resp?.message || "Failed to update decrement",
                      });
                    }
                  } catch (err: any) {
                    console.error("Update decrement error", err);
                    setErrorModal({
                      isOpen: true,
                      message: err?.message || "Failed to update decrement",
                    });
                  } finally {
                    setUpdatingDecrement(false);
                  }
                }}
                disabled={updatingDecrement}
              >
                {updatingDecrement ? "Updating..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="alv-participants-card">
        <div className="alv-participants-header">
          <Trophy className="alv-participants-icon" />
          <h3>Live Bids</h3>
          <div className="alv-participants-meta">
            <span className="alv-participant-count">
              <Users className="w-4 h-4" />
              {auction.statistics.active_participants} /{" "}
              {auction.statistics.total_participants} Participants
            </span>
            <span className="alv-live-status">
              <Activity className="w-4 h-4" />
              {auction.statistics.total_bids} Bids
            </span>
          </div>
        </div>

        {rankedBids.length > 0 ? (
          <div className="alv-table" role="table">
            <div className="alv-table-head" role="row">
              <span>Rank</span>
              <span>Participant</span>
              <span>Offered Price</span>
              <span>Bid Time</span>
              <span>Status</span>
              <span />
            </div>
            <div className="alv-participants-list">
              {rankedBids.map((bid, index) => {
                const when = formatBidTime(bid);
                return (
                  <div
                    key={bid.id}
                    role="row"
                    className={`alv-participant-item ${
                      index < 3 ? `alv-participant-l${index + 1}` : ""
                    }`}
                  >
                    <div className="alv-participant-rank">
                      <div className={`alv-rank-badge alv-rank-l${index + 1}`}>
                        {rankLabel(auction.auction_mode, index + 1)}
                      </div>
                    </div>

                    <div className="alv-participant-company">
                      <div className="alv-company-info">
                        <div className="alv-company-name">
                          <Contact className="w-4 h-4" />
                          <span>
                            {bid.person_name ||
                              bid.user?.person_name ||
                              "Unknown"}
                          </span>
                          {(bid as any).totalBids > 1 && (
                            <span
                              className="alv-chip alv-chip-bids"
                              title={`This participant has placed ${(bid as any).totalBids} bids in total`}
                            >
                              {(bid as any).totalBids} bids
                            </span>
                          )}
                        </div>
                        <div className="alv-company-address">
                          <Building2 className="w-4 h-4" />
                          <span>
                            {bid.company_name ||
                              bid.user?.company_name ||
                              "Unknown Company"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="alv-participant-bid">
                      <span className="alv-price-value">
                        {auction.currency}{" "}
                        {parseFloat(bid.amount).toLocaleString()}
                      </span>
                    </div>

                    <div className="alv-bid-time">
                      <span>{when.time}</span>
                      <span className="alv-bid-date">{when.date}</span>
                    </div>

                    <div className="alv-participant-status">
                      {index === 0 ? (
                        <div className="alv-status-leading">
                          <CheckCircle className="w-4 h-4" />
                          <span>Leading</span>
                        </div>
                      ) : (
                        <div className="alv-status-active">
                          <Eye className="w-4 h-4" />
                          <span>Active</span>
                        </div>
                      )}
                    </div>

                    <div className="alv-participant-actions">
                      <button
                        className="alv-btn alv-btn-danger alv-btn-sm"
                        onClick={() => handleRejectBid(bid.id)}
                        disabled={rejectingBidId === bid.id}
                      >
                        {rejectingBidId === bid.id
                          ? "Rejecting..."
                          : "Reject Bid"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="alv-no-participants">
            <Users className="alv-no-participants-icon" />
            <h4>No Bids Yet</h4>
            <p>Waiting for participants to place bids...</p>
          </div>
        )}
      </section>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => {
          setSuccessModal({ isOpen: false, title: "Success", message: "" });
        }}
        title={successModal.title}
        message={successModal.message}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => {
          setErrorModal({ isOpen: false, message: "" });
        }}
        title="Error"
        message={errorModal.message}
      />

      {/* Info Modal */}
      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={() => {
          setInfoModal({ isOpen: false, message: "" });
        }}
        title="Information"
        message={infoModal.message}
      />

      {/* Time Extension Modal */}
      {showTimeExtensionModal && (
        <div
          className="alv-modal-overlay"
          onClick={() => setShowTimeExtensionModal(false)}
        >
          <div
            className="alv-modal alv-modal-ext"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="alv-modal-ext-icon">
              <Clock />
            </div>
            <h3 className="alv-modal-title">Time Extension!</h3>
            <p>{timeExtensionMessage}</p>
            <div className="alv-modal-actions">
              <button
                className="alv-btn alv-btn-primary"
                onClick={() => setShowTimeExtensionModal(false)}
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctioneerLiveView;
